import { themeColors } from "../themeColors";
/**
 * @file   Source/gitcha_Web/src/helpers/common.js
 * @brief  This file is responsible for defing common functions used acroos the application
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import {
  BUY,
  DATE_FORMAT_MM_DD_YYYY,
  DEFAULT_MAP_ZOOM,
  DISPLAT_STR,
  DISPLAY_BUY,
  DISPLAY_LTR,
  DISPLAY_LTR_TIMELINE,
  DISPLAY_STR_PPTY_TYPE,
  DISPLAY_TEXT_BUY,
  DISPLAY_TEXT_LTR,
  DISPLAY_TEXT_STR,
  INTRODUCTION_EXPIRATION_TIME,
  INVENTORY_KEY,
  INVENTORY_PURPOSE,
  INVESTOR_MEMBER_PLAN,
  LONG_RENT,
  LOT_LAND,
  SCROLL_DIRECTION_DOWN,
  SHORT_RENT,
  USER_INFO_KEY,
  USER_TYPE_AGENT,
  WANT_AD_KEY,
} from "../constants/common";
import TimelineIcon from "../images/timeline-icon.svg";
import { convertToNumber } from "./validations";
import { isCurrentTenantMlsRestricted } from "./tenant";
import VisaIcon from "../images/visa-icon-sm.svg";
import MasterIcon from "../images/mastercard-icon.svg";
import AmexIcon from "../images/american-exp-icon.svg";
import noImage from "../images/no-image-placeholder.png";
import IntroductionVisaIcon from "../images/pm-visa-icon.svg";
import IntroductionMasterIcon from "../images/pm-mastercard-icon.svg";
import {
  getBuyerListUniqueFolderName,
  getGeoLocation,
  getMapStaticImage,
  getS3FolderName,
} from "../services/commonServices";
import { getPreSignedUrls } from "../services/fileUploadService";
import { getLotSizeList } from "../services/wantAdService";
import { EXACT_MOVE_DATE_TYPE } from "../constants/createWantAd";
import Resizer from "react-image-file-resizer";
import SchoolSmallIcon from "../images/school-icon.svg";
import NeighborhoodIcon from "../images/neighborhood-icon.svg";
import base64 from "base-64";
import utf8 from "utf8";
import { useLocation } from "react-router-dom";
import { format, parse, isValid } from "date-fns";
import moment from "moment";
import { ReactComponent as PrivateIcon } from "../images/privateIcon.svg";
import { ReactComponent as PriceIcon } from "../images/iconPrice.svg";

export const loggedInUser = () => {
  const user = getLocalStorage(USER_INFO_KEY);
  return user;
};

export const convertToCurrencySystem = (amount) => {
  // Nine Zeroes for Billions
  return Math.abs(convertToNumber(amount)) >= 1.0e9
    ? (Math.abs(convertToNumber(amount)) / 1.0e9).toFixed(2) * 1 + "B"
    : // Six Zeroes for Millions
      Math.abs(convertToNumber(amount)) >= 1.0e6
      ? (Math.abs(convertToNumber(amount)) / 1.0e6).toFixed(2) * 1 + "M"
      : // Three Zeroes for Thousands
        Math.abs(convertToNumber(amount)) >= 1.0e3
        ? (Math.abs(convertToNumber(amount)) / 1.0e3).toFixed(2) * 1 + "k"
        : Math.abs(convertToNumber(amount));
};

/**
 * function to add thousand seperator comma
 */
export const thousandSeperatedValue = (value) => {
  if (value) {
    value = value.toString().replace(/[A-Za-z!@#$%^&*()]/g, "");
    let newValue = value.replace(/,/g, "");
    return newValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  return null;
};

export const formatThousandSeparatedValue = (value) => {
  if (!value) return null;

  value = value.toString().replace(/\.00$/, "");
  value = value.replace(/[A-Za-z!@#$%^&*()]/g, "");
  const cleaned = value.replace(/,/g, "");

  return cleaned.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const thousandSeperatedValueFilter = (value) => {
  if (value) {
    value = value.toString().replace(/[A-Za-z!@#$%^&*()]/g, "");
    let newValue = value.replace(/,/g, "");
    return newValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  return value;
};

export const formatInventoryAddress = (address = "") => {
  if (!address) return "";
  return address.replace(/,\s*[\p{L}\s.'-]+$/u, "");
};

export const getAddressDisplay = (
  Location = "",
  City = "",
  State = "",
  ZIP = "",
) => {
  const cleaned = (Location || "")
    .replace(/,\s*(USA|United States)\s*$/i, "")
    .trim();

  const m = cleaned.match(
    /^(?:(?<street>[^,]+),\s*)?(?<city>[^,]+),\s*(?<state>[A-Z]{2})(?:\s+(?<zip>\d{5}(?:-\d{4})?))?$/i,
  );
  const groups = m?.groups || {};

  const city = (City || groups.city || "").trim();
  const state = (State || groups.state || "").trim().toUpperCase();
  const zip = (ZIP || groups.zip || "").trim();

  // Location doesn't always combine street + city/state (e.g. MLS rows pass
  // just the street, with city/state/zip supplied separately) - when it
  // doesn't match the combined pattern, treat it as the street itself.
  const street = (groups.street || (!m ? cleaned : "")).trim();

  const hasStreet = street && /\d/.test(street);

  const cityState = [city, state].filter(Boolean).join(", ");
  const cityStateZip = [cityState, zip].filter(Boolean).join(" ");

  if (hasStreet && city && state && zip) {
    return { before: street, after: cityStateZip };
  }

  return { before: cityState, after: "" };
};

export const checkIsNumber = (value) => {
  const numberReExp = /^[0-9\b]+$/;
  if (value) {
    const isValid = numberReExp.test(value);
    if (isValid) {
      return true;
    } else {
      return false;
    }
  }
  return true;
};

export const checkIsNumberWithDecimal = (value) => {
  const regExp = /^\d*\.?\d*$/;
  if (value) {
    const isValid = regExp.test(value);
    if (isValid) {
      return true;
    } else {
      return false;
    }
  }
  return true;
};

export const getStatePropertyValue = (property) => {
  const stateObject = getLocalStorage(WANT_AD_KEY);
  return _.get(stateObject, property, "");
};

export const getStateValue = (key, property) => {
  const stateObject = getLocalStorage(key);
  return _.get(stateObject, property, "");
};

const nth = function (d) {
  if (d > 3 && d < 21) return "th";
  switch (d % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const dateFns_FormatDate = (dte) => {
  //  dte - 2023-12-13 11:54:22
  const parsedDate = parse(dte, "yyyy-MM-dd HH:mm:ss", new Date());
  if (!isValid(parsedDate)) {
    console.log(" Invalid Date : ", dte);
    return "Invalid Date";
  }
  return format(parsedDate, "MM/dd/yy");
};

export const formateDate = (dte, format) => {
  if (format) {
    const dateToFormat = dte && new Date(dte);
    const dateObj = new Date(dte);
    const date = dateObj.getDate();
    const month = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "June",
      "July",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ][dateObj.getMonth()];
    switch (format) {
      case "mm/dd/yyyy":
        return [
          ("0" + (dateToFormat?.getMonth() + 1)).slice(-2),
          ("0" + dateToFormat?.getDate()).slice(-2),
          dateToFormat?.getFullYear(),
        ].join("/");
      case "mm/dd":
        return [
          ("0" + (dateToFormat.getMonth() + 1)).slice(-2),
          ("0" + dateToFormat.getDate()).slice(-2),
        ].join("/");

      case "shortMonth":
        const year = dateObj.getFullYear();
        var dateString = month + " " + date + nth(date) + ", " + year;
        return dateString;

      case "shortMonthWithDateOnly":
        var dateString = month + " " + date + nth(date);
        return dateString;

      case "mm/dd/yy": {
        return dateFns_FormatDate(dte);
      }
    }
  } else {
    const dateObj = new Date(dte);
    const date = dateObj.getDate();
    const month = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ][dateObj.getMonth()];
    const year = dateObj.getFullYear();
    var dateString = month + " " + date + nth(date);
    return dateString;
  }
};

export const monthNameForFlexibleDate = (id) => {
  const itemArray = [
    { month: "Jan", id: 1 },
    { month: "Feb", id: 2 },
    { month: "March", id: 3 },
    { month: "April", id: 4 },
    { month: "May", id: 5 },
    { month: "June", id: 6 },
    { month: "July", id: 7 },
    { month: "Aug", id: 8 },
    { month: "Sept", id: 9 },
    { month: "Oct", id: 10 },
    { month: "Nov", id: 11 },
    { month: "Dec", id: 12 },
  ];

  if (id) {
    const filteredArray = itemArray.filter((a) => a.id == id);
    if (filteredArray) {
      return _.get(filteredArray[0], "month", "");
    }
  } else {
    return itemArray;
  }
};

export const durationForFlexibleDate = (id) => {
  const itemArray = [
    { duration: "Day", id: 1 },
    { duration: "Weekend", id: 2 },
    { duration: "Week", id: 3 },
    { duration: "Month", id: 4 },
  ];

  if (id) {
    const filteredArray = itemArray.filter((a) => a.id == id);
    if (filteredArray) {
      return _.get(filteredArray[0], "duration", "");
    }
  } else {
    return itemArray;
  }
};

export const durationForFlexibleDateResponsive = (id) => {
  const itemArray = [
    { duration: "Day", id: 1 },
    { duration: "Weekend", id: 2 },
    { duration: "Week", id: 3 },
    { duration: "Month", id: 4 },
  ];

  if (id) {
    const filteredArray = itemArray.filter((a) => a.id == id);
    if (filteredArray) {
      return _.get(filteredArray[0], "duration", "");
    }
  } else {
    return "";
  }
};

export const monthNameForFlexibleDateResponsive = (id) => {
  const itemArray = [
    { month: "Jan", id: 1 },
    { month: "Feb", id: 2 },
    { month: "March", id: 3 },
    { month: "April", id: 4 },
    { month: "May", id: 5 },
    { month: "June", id: 6 },
    { month: "July", id: 7 },
    { month: "Aug", id: 8 },
    { month: "Sept", id: 9 },
    { month: "Oct", id: 10 },
    { month: "Nov", id: 11 },
    { month: "Dec", id: 12 },
  ];

  if (id) {
    const filteredArray = itemArray.filter((a) => a.id == id);
    if (filteredArray) {
      return _.get(filteredArray[0], "month", "");
    }
  } else {
    return "";
  }
};

export const loadPreferredLocationHtml = (formValues, isMobileView) => {
  const highSchool = _.get(formValues, "highSchool", null);
  const neighborHood = _.get(formValues, "neighborHood", null);

  return (
    <>
      {highSchool && (
        <li>
          <span
            className={isMobileView ? "list-icn-wrap-mob" : "list-icn-wrap"}
          >
            <img src={SchoolSmallIcon} />
          </span>
          {isMobileView ? "Preferred School: " + highSchool : highSchool}
        </li>
      )}

      {neighborHood && (
        <li>
          <span
            className={isMobileView ? "list-icn-wrap-mob" : "list-icn-wrap"}
          >
            <img src={NeighborhoodIcon} />
          </span>
          {isMobileView
            ? "Preferred Neighborhood: " + neighborHood
            : neighborHood}
        </li>
      )}
    </>
  );
};

export const loadTimeLineHtml = (
  adType,
  adTypeBasedOnProperty,
  formValues,
  isMobileView,
) => {
  let timeLine;
  if (adType === BUY) {
    timeLine = _.get(formValues, "tlText", "");
  } else if (adType === LONG_RENT) {
    const moveInTimeLine = convertToNumber(
      _.get(formValues, "moveInTimeLine", ""),
    );
    const moveInDate = _.get(formValues, "startDate", "");
    if (moveInTimeLine === EXACT_MOVE_DATE_TYPE && moveInDate) {
      timeLine =
        "Move-in by " + formateDate(moveInDate, "shortMonthWithDateOnly");
    } else {
      timeLine = `${_.get(formValues, "moveInTxt", "")}`;
    }
  } else if (adType === SHORT_RENT && adTypeBasedOnProperty === LOT_LAND) {
    if (
      _.get(formValues, "startDate", "") &&
      _.get(formValues, "endDate", "")
    ) {
      timeLine =
        formateDate(_.get(formValues, "startDate", ""), "mm/dd/yyyy") +
        " - " +
        formateDate(_.get(formValues, "endDate", ""), "mm/dd/yyyy");
    } else {
      timeLine =
        durationForFlexibleDate(_.get(formValues, "duration", "")) +
        " - " +
        monthNameForFlexibleDate(_.get(formValues, "month", ""));
    }
  }
  if (
    (adType === SHORT_RENT && adTypeBasedOnProperty === LOT_LAND) ||
    adType !== SHORT_RENT
  ) {
    return (
      <li>
        <span className={isMobileView ? "list-icn-wrap-mob" : "list-icn-wrap"}>
          <img src={TimelineIcon} alt={TimelineIcon} />
        </span>
        Timeline: {timeLine}
      </li>
    );
  } else {
    return "";
  }
};

export const loadTimeLineString = (adType, formValues) => {
  let timeLine = "";
  if (adType === BUY) {
    timeLine = _.get(formValues, "tlText", "");
  } else if (adType === LONG_RENT) {
    const moveInTimeLine = convertToNumber(
      _.get(formValues, "moveInTimeLine", ""),
    );
    const moveInDate = _.get(formValues, "startDate", "");
    if (moveInTimeLine === EXACT_MOVE_DATE_TYPE && moveInDate) {
      timeLine =
        "Move-in by " + formateDate(moveInDate, "shortMonthWithDateOnly");
    } else {
      timeLine = `${_.get(formValues, "moveInTxt", "")}`;
    }
  } else if (adType === SHORT_RENT) {
    if (
      _.get(formValues, "startDate", null) &&
      _.get(formValues, "endDate", null)
    ) {
      timeLine =
        formateDate(_.get(formValues, "startDate", ""), "mm/dd/yyyy") +
        " - " +
        formateDate(_.get(formValues, "endDate", ""), "mm/dd/yyyy");
    } else if (
      _.get(formValues, "duration", null) &&
      _.get(formValues, "month", null)
    ) {
      timeLine =
        durationForFlexibleDateResponsive(_.get(formValues, "duration", "")) +
        " in " +
        monthNameForFlexibleDateResponsive(_.get(formValues, "month", ""));
    } else {
      timeLine = "";
    }
  }
  return timeLine;
};

export const capitalizeFirstString = (str) => {
  return str && str.charAt(0).toUpperCase() + str.slice(1);
};

export const upperCaseEachSentenceFirst = (input, lowercaseBefore) => {
  input = input === undefined || input === null ? "" : input;
  if (lowercaseBefore) {
    input = input.toLowerCase();
  }
  return input
    .toString()
    .replace(/(^|\. *)([a-z])/g, function (match, separator, char) {
      return separator + char.toUpperCase();
    });
};

export const replaceString = (str, replacingChar, charToBeReplcing) => {
  if (charToBeReplcing) {
    return str && str.replace(replacingChar, charToBeReplcing);
  } else {
    return str && str.replace(replacingChar, "");
  }
};

export const replaceComma = (str) => {
  const inputString = str && str.toString().replace(/,/g, "");
  if (!isNaN(inputString)) {
    return inputString;
  } else {
    const matchedString = inputString.match(/\d+/);
    return matchedString ? matchedString[0] : "";
  }
};

export function truncate(string, n) {
  return string?.length > n ? string.slice(0, n - 1) + "..." : string;
}

export function isIphone() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

export const getAgentStateValue = (property) => {
  const stateObject = JSON.parse(localStorage.getItem("agentWantAdData"));
  return _.get(stateObject, property, "");
};

export const getLocalStorage = (key) => {
  if (key) {
    // A corrupt entry used to throw out of render and white-screen the app.
    try {
      return JSON.parse(localStorage.getItem(key));
    } catch (e) {
      localStorage.removeItem(key);
      return null;
    }
  } else {
    return {};
  }
};

export const setLocalStorage = (key, value) => {
  if (value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.warn(`Unable to persist "${key}" to localStorage`, e);
    }
  }
};

export const removeLocalStorage = (key) => {
  if (key) {
    localStorage.removeItem(key);
  }
};

export const clearLocalStorage = () => {
  localStorage.clear();
};

export const clearLocalStorageOnLogout = () => {
  Object.keys(localStorage).forEach((key) => {
    if (key != "userLocation" && key !== "welcomeModalShown") {
      localStorage.removeItem(key);
    }
  });
  sessionStorage.removeItem("homeFilterSession");
};

export const generateBase64AgentId = (agentId) => {
  return btoa(`gitcha-${agentId}`);
};

export const trimString = (inputString) => {
  if (inputString) {
    return inputString.trim();
  }
  return "";
};

export const checkIsNull = (inputString) => {
  if (inputString) {
    return trimString(inputString) === "";
  }
  return false;
};

export const filterArray = (array, key, value) => {
  const filteredArray = array.filter(function (co) {
    return co[key] === value;
  });
  return _.get(filteredArray[0], "text", null);
};

export const itemList = (itemArray, filterId, filterKey) => {
  if (filterId) {
    filterArray(itemArray, filterKey, filterId);
  } else {
    return itemArray;
  }
};

export const filterArrayBasedOnKey = ({
  list,
  filterKey,
  value,
  isNotEqual = false,
}) => {
  if (list) {
    const filterArray = list.filter(function (lst) {
      if (isNotEqual) {
        return lst[filterKey] !== value;
      }
      return lst[filterKey] == value;
    });
    return filterArray;
  }
  return null;
};

export const getFileType = (filename) => {
  return filename.split(".").pop();
};

export const loadPropertTypeImage = (isRepresentingAsSeller) => {
  return isRepresentingAsSeller ? (
    <PrivateIcon width="18" height="18" />
  ) : (
    <PriceIcon width="18" height="18" />
  );
};

export function toTitleCase(str) {
  return str?.replace(/\w\S*/g, function (txt) {
    return txt?.charAt(0).toUpperCase() + txt?.substr(1);
  });
}

export const loadPhotos = (photos, className) => {
  const photosCount = photos.length;
  var dom_content = [];
  if (photos.length === 0) {
    return (
      <div className={className || "inventory-banner position-relative mt-30"}>
        <div className="banner-row">
          {" "}
          <div className="banner-column-full w-100 h-204">
            <div className="full-col-img-wrap h-100 placeholder-wrap-bg brdr-8">
              <img
                className="brdr-8 mw-100 placeholder-img-style"
                src={noImage}
                alt={noImage}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (photosCount > 0) {
    if (photosCount === 1) {
      dom_content.push(
        <div className="banner-column-full h-204">
          <div className="full-col-img-wrap h-204">
            <img
              className="brdr-8 w-100 col-img-style"
              src={_.get(photos[0], "image", "")}
              alt={_.get(photos[0], "image", "")}
            />
          </div>
        </div>,
      );
    }
    if (photosCount === 2) {
      dom_content.push(
        <>
          <div className="banner-column-half h-204">
            <div className="half-col-img-wrap h-204">
              <img
                className="brdr-top-left-8 brdr-btm-left-8 brdr-t-r-8 no-brdr-b-l-0 col-img-style"
                src={_.get(photos[0], "image", "")}
              />
            </div>
          </div>
          <div className="banner-column-half h-204">
            <div className="half-col-img-wrap-right h-204">
              <img
                className="brdr-top-right-8 brdr-btm-right-8 no-brdr-t-r-0 brdr-b-r-8 brdr-b-l-8 col-img-style"
                src={_.get(photos[1], "image", "")}
              />
            </div>
          </div>
        </>,
      );
    }

    if (photosCount === 3) {
      dom_content.push(
        <>
          <div className="banner-column-twothird h-204">
            <div className="half-col-img-wrap h-204">
              <img
                className="brdr-top-left-8 brdr-btm-left-8 brdr-t-r-8 no-brdr-b-l-0 col-img-style"
                src={_.get(photos[0], "image", "")}
              />
            </div>
          </div>
          <div className="banner-column-onethird h-204">
            <div className="quarter-col-img-wrap-top h-50-percent rh-204">
              <img
                className="brdr-top-right-8 no-brdr-t-r-0 col-img-style"
                src={_.get(photos[1], "image", "")}
              />
            </div>
            <div className="quarter-col-img-wrap-btm h-50-percent rh-204">
              <img
                className="brdr-btm-right-8 brdr-b-l-8 col-img-style"
                src={_.get(photos[2], "image", "")}
              />
            </div>
          </div>
        </>,
      );
    }
    if (photosCount === 4) {
      dom_content.push(
        <>
          <div className="banner-column-half h-204">
            <div className="half-col-img-wrap h-204">
              <img
                src={_.get(photos[0], "image", "")}
                className="brdr-top-left-8 brdr-btm-left-8 brdr-t-r-8 no-brdr-b-l-0 col-img-style"
              />
            </div>
          </div>

          <div className="banner-column-30percent h-204">
            <div className="percent30-col-img-wrap h-204">
              <img
                src={_.get(photos[1], "image", "")}
                className="col-img-style"
              />
            </div>
          </div>
          <div className="banner-column-20percent h-204">
            <div className="quarter-col-img-wrap-top h-50-percent rh-204">
              <img
                src={_.get(photos[2], "image", "")}
                className="brdr-top-right-8 no-brdr-t-r-0 col-img-style"
              />
            </div>

            <div className="quarter-col-img-wrap-btm h-50-percent rh-204">
              <img
                src={_.get(photos[3], "image", "")}
                className="brdr-btm-right-8 brdr-b-l-8 col-img-style"
              />
            </div>
          </div>
        </>,
      );
    }
    if (photosCount >= 5) {
      dom_content.push(
        <>
          <div className="banner-column-half h-204">
            <div className="half-col-img-wrap h-204">
              <img
                className="brdr-top-left-8 brdr-btm-left-8 brdr-t-r-8 no-brdr-b-l-0 col-img-style"
                src={_.get(photos[0], "image", "")}
              />
            </div>
          </div>

          <div className="banner-column-quarter h-204">
            <div className="quarter-col-img-wrap-top h-50-percent rh-204">
              <img
                className="col-img-style"
                src={_.get(photos[1], "image", "")}
              />
            </div>
            <div className="quarter-col-img-wrap-btm h-50-percent rh-204">
              <img
                className="col-img-style"
                src={_.get(photos[2], "image", "")}
              />
            </div>
          </div>

          <div className="banner-column-quarter h-204">
            <div className="quarter-col-img-wrap-top h-50-percent rh-204">
              <img
                className="brdr-top-right-8 no-brdr-t-r-0 col-img-style"
                src={_.get(photos[3], "image", "")}
              />
            </div>
            <div className="quarter-col-img-wrap-btm h-50-percent rh-204 col-pad-btm">
              <img
                className="brdr-btm-right-8 brdr-b-l-8 col-img-style"
                src={_.get(photos[4], "image", "")}
              />
            </div>
          </div>
        </>,
      );
    }
    return (
      <div className={className || "inventory-banner position-relative mt-30"}>
        {photosCount > 5 ? (
          <div className="upload-cover-btn upload-cover-btn-pos">
            +{photosCount - 5}
          </div>
        ) : (
          ""
        )}

        <div className="banner-row">{dom_content}</div>
      </div>
    );
  } else {
    return "";
  }
};
export const getUserId = () => {
  const user = getLocalStorage(USER_INFO_KEY);
  return _.get(user, "userInfo.userId", null);
};

export const getUserType = () => {
  const user = getLocalStorage(USER_INFO_KEY);
  return _.get(user, "userInfo.userType", null);
};

export const getUserName = () => {
  const user = getLocalStorage(USER_INFO_KEY);
  return _.get(user, "userInfo.name", null);
};

export const formatResponse = (value) => {
  if (value) {
    value = JSON.parse(JSON.stringify(value));
    value.forEach((obj) => renameKey(obj, "Id", "value"));
    value.forEach((obj) => renameKey(obj, "Text", "label"));
    return value;
  }
  return null;
};

/**
 * To rename the key names to new name
 */
export const renameKey = (obj, oldKey, newKey) => {
  obj[newKey] = obj[oldKey];
  delete obj[oldKey];
};

/**
 * To combine the key names to new key
 */
export const combineKey = (obj, oldKey1, oldKey2, newKey) => {
  obj[newKey] = obj[oldKey1] + " " + obj[oldKey2];
};

/* To get the card icon */
export const getCardIcon = (brand) => {
  switch (brand) {
    case "Visa":
      return VisaIcon;

    case "MasterCard":
      return MasterIcon;
    case "American Express":
      return AmexIcon;
    default:
      return null;
  }
};

/* To get the card icon */
export const getCardIconForIntroduction = (brand) => {
  switch (brand) {
    case "Visa":
      return IntroductionVisaIcon;
    case "MasterCard":
      return IntroductionMasterIcon;
    case "American Express":
      return AmexIcon;
    default:
      return null;
  }
};

export const getInitials = (name) => {
  let initials = null;
  let backgroundColor = null;
  let firstLetter = null;
  const nameSplit = name.split(" ");
  const nameLength = nameSplit.length;

  if (nameLength > 1) {
    const firstChars = name
      .split(" ")
      .map((word) => word[0])
      .join("");
    // initials = firstChars.substring(0, 2)
    initials = firstChars.substring(0, 2); // considered only one character bcz group name has no space
    firstLetter = firstChars.substring(0, 1); // considered only one character bcz group name has no space
  } else if (nameLength === 1) {
    initials = nameSplit[0].substring(0, 2);
    firstLetter = nameSplit[0].substring(0, 1);
  } else return;

  firstLetter = firstLetter.toUpperCase();

  if (
    firstLetter == "A" ||
    firstLetter == "F" ||
    firstLetter == "K" ||
    firstLetter == "P" ||
    firstLetter == "U"
  )
    backgroundColor = "profile-bg-green";
  else if (
    firstLetter == "B" ||
    firstLetter == "G" ||
    firstLetter == "L" ||
    firstLetter == "Q" ||
    firstLetter == "V"
  )
    backgroundColor = "profile-bg-orange";
  else if (
    firstLetter == "C" ||
    firstLetter == "H" ||
    firstLetter == "M" ||
    firstLetter == "R" ||
    firstLetter == "W"
  )
    backgroundColor = "profile-bg-meroon";
  else if (
    firstLetter == "D" ||
    firstLetter == "I" ||
    firstLetter == "N" ||
    firstLetter == "S" ||
    firstLetter == "X"
  )
    backgroundColor = "profile-bg-paleblue";
  else if (
    firstLetter == "E" ||
    firstLetter == "J" ||
    firstLetter == "O" ||
    firstLetter == "T" ||
    firstLetter == "Y"
  )
    backgroundColor = "profile-bg-turquoise";
  else backgroundColor = "profile-bg-green";

  let returnData = {
    backgroundColor: backgroundColor,
    initials: initials.toUpperCase(),
  };

  return returnData;
};

export const isDateInThePast = (month, year) => {
  const today = new Date();
  let currentMonth = String(today.getMonth() + 1).padStart(2, "0");
  let currentYear = today.getFullYear();
  let newMonth = String(month).padStart(2, "0");
  if (year > currentYear) {
    return false;
  } else if (year == currentYear) {
    if (newMonth >= currentMonth) {
      return false;
    } else {
      return true;
    }
  } else {
    return true;
  }
};

export const sortItems = (order, property) => (a, b) => {
  const itemA = _.get(a, property);
  const itemB = _.get(b, property);
  if (order === "asc") {
    return itemA < itemB ? -1 : 1;
  } else {
    return itemA < itemB ? 1 : -1;
  }
};

export const timeAgo = (date) => {
  var ms = new Date().getTime() - new Date(date).getTime();
  var seconds = Math.floor(ms / 1000);
  var minutes = Math.floor(seconds / 60);
  var hours = Math.floor(minutes / 60);
  var days = Math.floor(hours / 24);
  var months = Math.floor(days / 30);
  var years = Math.floor(months / 12);
  let test = "";
  if (ms <= 0) {
    return "Just now";
  }
  if (seconds < 60) {
    return seconds + " seconds Ago";
  }
  if (minutes < 60) {
    return minutes + " minutes Ago";
  }
  if (hours < 24) {
    return hours + " hours Ago";
  }
  if (days < 30) {
    return days + " days Ago";
  }
  if (months < 12) {
    return months + " months Ago";
  } else {
    return years + " years Ago";
  }
};

export const getPeriod = (num) => {
  if (num === 3) {
    return "year";
  } else if (num === 2) {
    return "month";
  } else return "free";
};

export const getPeriod_Mobile = (num) => {
  if (num === 3) {
    return "Annual";
  } else if (num === 2) {
    return "Monthly";
  } else return "Free";
};

export const getPlanName_Mobile = (userTypeId, period) => {
  if (period === 3) {
    return "Premium";
  } else if (period === 2) {
    return "Premium";
  } else {
    if (userTypeId === 1) return "Free";
    else return "Member";
  }
};

export const getpropertyName = (jsonObject) => {
  const propertyName = _.get(jsonObject, "PropertyTypeText", "");
  const subPropertyName = _.get(jsonObject, "Sub_PropertyTypeText", "")
    ? _.get(jsonObject, "Sub_PropertyTypeText", "")
    : _.get(jsonObject, "SubPropertyTypeText", "");
  if (subPropertyName !== "null" && subPropertyName) {
    return subPropertyName;
  }
  return propertyName;
};

export const checkNotNull = (item) => {
  if ((item && item !== "null") || item !== "undefined") {
    return true;
  } else {
    return false;
  }
};

export const handleLogOut = () => {
  clearLocalStorage();
  window.location.reload(false);
};

export const removeMultipleLocalStorageItems = (keysToRemove) => {
  keysToRemove.forEach((k) => localStorage.removeItem(k));
};

export const removeDecimalZeros = (value) => {
  if (value) {
    return value * 1;
  }
  return null;
};

export const removeDecimalZerosFromTextField = (value) => {
  if (value) {
    return value.toString().replace(/\.00$/, "");
  }
  return null;
};

export const createS3Folder = async (isWantAd = false) => {
  return await getS3FolderName({ isWantAd: isWantAd })
    .then((response) => {
      const result = _.get(response, "result", null);
      if (response.errorCode === 0 && result) {
        const folderName = _.get(result, "folderName", "");
        return folderName;
      }
    })
    .catch((err) => {
      console.log("Error", err);
    });
};

export const createS3FolderForBuyersListingService = async (
  client_Want_ad_Id,
  isWantAd = false,
) => {
  if (client_Want_ad_Id) {
    return await getBuyerListUniqueFolderName({
      client_Want_ad_Id: client_Want_ad_Id,
      isWantAd: isWantAd,
    })
      .then((response) => {
        const result = _.get(response, "result", null);
        if (response.errorCode === 0 && result) {
          const folderName = _.get(result, "folderName", "");
          return folderName;
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  }
};

export const createStaticMapImage = async (
  lat,
  lng,
  mapZoom = DEFAULT_MAP_ZOOM,
  isFromWantAd,
) => {
  const mapImgResolution = "800x400";
  const requestPayload = {
    latitude: lat,
    longitude: lng,
    zoom: mapZoom,
    size: mapImgResolution,
    format: "jpeg",
    isFromWantAd: isFromWantAd || false,
  };
  return await getMapStaticImage(requestPayload)
    .then((response) => {
      return response.blob();
    })
    .catch((err) => {
      console.log("Error", err);
    });
};

export const createPreSignedUrl = async (
  isWantAd = false,
  s3FolderName = "",
  isVideo = false,
) => {
  const baseFolderPath = isWantAd ? "WantAdMapImages/" : "/InventoryImages/";
  const details = getLocalStorage(isWantAd ? WANT_AD_KEY : INVENTORY_KEY);
  const s3Folder = s3FolderName
    ? s3FolderName
    : _.get(details, "s3ImageFolder", null);
  let fileName =
    s3Folder + (isWantAd ? "_wantAdMapImg.jpeg" : "_inventoryMapImg.jpeg");
  if (isVideo && isWantAd) {
    fileName = s3Folder + "_wantAdVideo.mp4";
  }
  const imgPath = baseFolderPath + s3Folder + "/" + fileName;
  const reqPayload = !s3Folder
    ? {
        folder: baseFolderPath + s3Folder + "/",
        keys: [{ id: 1, path: imgPath }],
      }
    : { keys: [{ id: 1, path: imgPath }] };
  const response = await getPreSignedUrls(reqPayload);
  return response;
};
export const createPreSignedUrlForVideo = async (
  isWantAd = false,
  s3FolderName = "",
) => {
  const baseFolderPath = isWantAd ? "WantAdVideos/" : "InventoryVideos/";
  const details = getLocalStorage(isWantAd ? WANT_AD_KEY : INVENTORY_KEY);
  const s3Folder = s3FolderName
    ? s3FolderName
    : _.get(details, "s3ImageFolder", null);
  // Construct the filename for the video
  const fileName = `${s3Folder}${
    isWantAd ? "_wantAdVideo.mp4" : "_inventoryVideo.mp4"
  }`;
  const videoPath = `${baseFolderPath}${s3Folder}/${Math.floor(
    1000 + Math.random() * 9000,
  )}/${fileName}`;

  // Prepare payload for pre-signed URL request
  const reqPayload = !s3Folder
    ? {
        folder: `${baseFolderPath}${s3Folder}/`,
        keys: [{ id: 1, path: videoPath }],
      }
    : { keys: [{ id: 1, path: videoPath }] };

  // Fetch pre-signed URLs
  console.log({ reqPayload });
  const response = await getPreSignedUrls(reqPayload);
  return response?.result?.[0]?.presignedUrl;
};

export const yearValidation = (year) => {
  var regExpression = /^[0-9]+$/;
  if (year.length === 4) {
    if (regExpression.test(year)) {
      var current_year = new Date().getFullYear();
      if (year < 1900 || year > current_year) {
        return false;
      }
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
};

export const getLotSizeLists = async () => {
  return await getLotSizeList()
    .then((response) => {
      if (response.errorCode === 0 && _.get(response, "result", null)) {
        const list = _.get(response, "result", null);
        let newList = [];
        list.map((lst) => {
          const value = _.get(lst, "Lot_Value");
          const text = _.get(lst, "LotSz_TexT", "");
          newList.push({ value: value, label: text });
        });
        return newList;
      }
    })
    .catch((err) => {
      return [];
    });
};

export const removeFixedDecimalPart = ({ value, decimalPoint }) => {
  if (value && decimalPoint) {
    return value.toFixed(decimalPoint);
  }
};

export const isValidDate = ({ dateString, format }) => {
  let dateRegex;
  if (format === DATE_FORMAT_MM_DD_YYYY) {
    dateRegex = /^(0[1-9]|1[0-2])[/.-](0[1-9]|[12][0-9]|3[01])[/.-](\d{4})$/;
  }
  if (!dateRegex.test(dateString)) {
    return false; // Invalid format
  }
  var selectedDate = new Date(dateString);
  var currentDate = new Date();
  if (selectedDate < currentDate) {
    return false; // Invalid format
  }
  return true;
};

export const getLocalStorageValue = ({
  localStorageKey,
  searchKey,
  defaultValue,
}) => {
  if (localStorageKey && searchKey) {
    return _.get(
      getLocalStorage(localStorageKey),
      searchKey,
      defaultValue || null,
    );
  }
  return null;
};

export const getPriceText = (adType) => {
  if (adType === "LTR") {
    return "Price(monthly)";
  } else if (adType === "STR") {
    return "Price(nightly)";
  } else {
    return "Price";
  }
};

export const getMeters = (miles) => {
  if (miles) {
    return miles * 1609.344;
  }
  return null;
};

export const getCookie = (cname) => {
  let name = cname + "=";
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
};

export const arraySplice = (list, index) => {
  if (list?.length) {
    return list.slice(0, index);
  }
  return [];
};

export const sendEmailContactSupport = (message) => {
  const email = "support@gitcha.com";
  const subject = "";
  const emailBody = "Hi Gitcha Support Team,";
  window.location.href =
    "mailto:" + email + "?subject=" + subject + "&body=" + emailBody;
};

export const sendEmailSales = (message) => {
  const email = "sales@gitcha.com";
  const subject = "";
  const emailBody = "Hi Gitcha Sales Team,";
  window.location.href =
    "mailto:" + email + "?subject=" + subject + "&body=" + emailBody;
};

export const sendEmailShareUrl = (email, clientName, link) => {
  const subject = "";
  const emailBody =
    "Hi " +
    clientName +
    ",\n Please click the link to add the want ad details" +
    link;
  window.location.href =
    "mailto:" + email + "?subject=" + subject + "&body=" + emailBody;
};

export const reSizeImage = ({ file, width, height, fileType }) =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      width,
      height,
      fileType,
      100,
      0,
      (uri) => {
        resolve(uri);
      },
      "blob",
    );
  });

export const getPlanPeriodText = (period) => {
  if (period === 3) {
    return "annual";
  } else if (period === 2) {
    return "monthly";
  } else {
    return "";
  }
};

export const checkTutorialDisplayStatus = (page) => {
  // 1= Home page left side menu tutorial
  // 2 = Introduction dropdown tutorial in header dropdown;
  // 3 = Introduction details popup tutorial
  // 4 = Agent dashboard tutorial
  // 5= Wantadd display tutorial
  // 6= Wantadd add  tutorial
  // 7= Inventory(property) add  tutorial
  // 8= Inventory drafts tutorial
  // 9= WantAd Drafts tutorial
  // 10=Pro Dashboard Tutorial

  // BLS tenants are pre-verified MLS agent seats and should never see the
  // legacy Gitcha first-time tutorial overlays. Help icons and the BLS empty
  // states are separate surfaces and are unaffected by this gate.
  if (isCurrentTenantMlsRestricted()) return false;

  let tutorialDisplayedPages = getLocalStorage("tutorialDisplayedPages");
  let showNotification =
    tutorialDisplayedPages && tutorialDisplayedPages.includes(page)
      ? false
      : true;
  return showNotification;
};

export const saveTutorialStatusToLocal = (page) => {
  // 1= Home page left side menu tutorial
  // 2 = Introduction dropdown tutorial in header dropdown;
  // 3 = Introduction details popup tutorial
  // 4 = Agent dashboard tutorial
  // 5= Wantadd display tutorial
  // 6= Wantadd add  tutorial
  // 7= Inventory(property) add  tutorial
  let tutorialDisplayedPages = getLocalStorage("tutorialDisplayedPages");
  tutorialDisplayedPages = tutorialDisplayedPages ? tutorialDisplayedPages : [];
  if (!tutorialDisplayedPages.includes(page)) {
    tutorialDisplayedPages.push(page);
    setLocalStorage("tutorialDisplayedPages", tutorialDisplayedPages);
  }
  return true;
};

export const generateFileName = () => {
  let result = "";
  const list = "abcedefghijklmnopqrstuvwxyz123456789";
  for (let i = 0; i < 7; i++) {
    var rnd = Math.floor(Math.random() * list.length);
    result = result + list.charAt(rnd);
  }
  return result;
};

export const handleMobileUserLogin = () => {
  const userDetails = getLocalStorage(USER_INFO_KEY);
  const userType = _.get(userDetails, "userInfo.userType", null);
  const agentTitle = _.get(userDetails, "userInfo.agentTitle", null);
  const isVerified = _.get(userDetails, "userInfo.userIsVerified", null);
  const planStatus = _.get(userDetails, "userInfo.planStatus", null);
  const userStatus = _.get(userDetails, "userInfo.userStatus", null);
  let userLoginStatus = { isValidLogin: false, userType: userType };
  if (userDetails) {
    if (userType) {
      if (isVerified && planStatus && userStatus) {
        userLoginStatus.isValidLogin = true;
      } else {
        if (convertToNumber(userType) === USER_TYPE_AGENT) {
          if (!agentTitle) {
            userLoginStatus.noAgentTitle = true;
          } else if (!planStatus) {
            userLoginStatus.noAgentPlanSelected = true;
          } else if (!userStatus) {
            userLoginStatus.noAgentUserDetails = true;
          }
        } else {
          if (!planStatus) {
            userLoginStatus.noMemberPlanSelected = true;
          } else if (!userStatus) {
            userLoginStatus.noMemberUserDetails = true;
          }
        }
      }
    } else {
      userLoginStatus.noUserType = true;
    }
  }
  return userLoginStatus;
};
export const getAdTypeText = (adType) => {
  if (adType === BUY) {
    return DISPLAY_TEXT_BUY;
  } else if (adType === LONG_RENT) {
    return DISPLAY_TEXT_LTR;
  } else if (adType === SHORT_RENT) {
    return DISPLAY_TEXT_STR;
  }
};

export const displayAd = (adType) => {
  if (adType === BUY) {
    return DISPLAY_BUY;
  } else if (adType === LONG_RENT) {
    return DISPLAY_LTR;
  } else if (adType === SHORT_RENT) {
    return DISPLAT_STR;
  }
};

export const displayAdTimeLine = (adType) => {
  if (adType === BUY) {
    return DISPLAY_BUY;
  } else if (adType === LONG_RENT) {
    return DISPLAY_LTR_TIMELINE;
  } else if (adType === SHORT_RENT) {
    return DISPLAT_STR;
  }
};

export const displayPropertyType = (adType) => {
  if (adType === BUY) {
    return DISPLAY_BUY;
  } else if (adType === LONG_RENT) {
    return DISPLAY_LTR;
  } else if (adType === SHORT_RENT) {
    return DISPLAY_STR_PPTY_TYPE;
  }
};

export const decryption = (value) => {
  const bytes = base64.decode(value);
  const decodedId = utf8.decode(bytes);
  if (decodedId) {
    const decodedData = decodedId.split("-");
    return decodedData.length > 0 ? decodedData[1] : null;
  }
  return null;
};

export const getCurrentPath = () => {
  const location = useLocation();
  const pathName = _.get(location, "pathname", null)?.toLowerCase();
  return pathName;
};

export const checkArrayisEqual = (a, b) => {
  // check if the lengths are equal
  if (a?.length !== b?.length) {
    return false;
  }

  // iterate over first array
  for (let i of a) {
    // if current element in the first array is not present in the second array
    if (b?.indexOf(i) === -1) {
      return false;
    }
  }

  return true;
};

export const getClientLogo = (firstName, lastName) => {
  let clientName = lastName + " " + firstName;
  if (clientName) {
    clientName = clientName.replace(/-/g, "").match(/\b(\w)/g);
  }
  return clientName;
};

export const getPropertyPurpose = (
  is_Seller,
  is_ShortTerm_Renter,
  is_LongTerm_Renter,
) => {
  const proprtyPurpose = [];
  if (is_Seller) proprtyPurpose.push(INVENTORY_PURPOSE.Seller);
  if (is_ShortTerm_Renter)
    proprtyPurpose.push(INVENTORY_PURPOSE.Short_Term_Rent);
  if (is_LongTerm_Renter) proprtyPurpose.push(INVENTORY_PURPOSE.Long_Term_Rent);

  return proprtyPurpose;
};

export const decodeParam = (param) => {
  let decodedParam;
  let paramDecoded;
  try {
    const bytes = base64.decode(param);
    decodedParam = utf8.decode(bytes);
    if (decodedParam) {
      let decodedParamData = decodedParam.split("-");
      paramDecoded = decodedParamData.length > 0 ? decodedParamData[1] : null;
    }
  } catch {}

  return paramDecoded;
};

export const classesBasedOnScroll = (scrollDirection) => {
  let mainWrapperClass =
    "gitcha-main-wrapper my-client-wrap view-client-wrap pb-90 windowscroll-fix";
  let contentWrapperClass =
    "content-wrapper content-wrapper-h-90-percent windowscroll-fix";
  if (scrollDirection === SCROLL_DIRECTION_DOWN) {
    mainWrapperClass =
      "gitcha-main-wrapper my-client-wrap view-client-wrap pad-btm-0 windowscroll-fix";
    contentWrapperClass = "content-wrapper h-100 windowscroll-fix";
  }
  return {
    mainWrapperClass: mainWrapperClass,
    contentWrapperClass: contentWrapperClass,
  };
};

export const setBodyBackgroundWhite = () => {
  document.body.style.background = "white";
};

export const setBodyBackgroundGrey = () => {
  document.body.style.background = "#f9f9fa";
};

export const setBodyBackgroundDarkGrey = () => {
  document.body.style.background = "#e6eaf2";
};

export function formatPhoneNumber(value) {
  if (!value) return value;

  const phoneNumber = value.replace(/[^\d]/g, "");

  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < 4) return phoneNumber;

  if (phoneNumberLength < 7) {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  }
  return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(
    3,
    6,
  )}-${phoneNumber.slice(6, 10)}`;
}

export function formatDisplayPhoneNumber(value) {
  if (!value) return value;

  const phoneNumber = value.replace(/[^\d]/g, "");

  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < 4) return phoneNumber;

  if (phoneNumberLength < 7) {
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6,
  )}-${phoneNumber.slice(6, 10)}`;
}

export const setHtmlClassName = (className) => {
  document.getElementById("scroll-chain").classList.add(className);
};

export const removeHtmlClassName = (className) => {
  document.getElementById("scroll-chain").classList.remove(className);
};

export const getDisplayTextForClientFilledWantAds = (
  IsFilledByClient,
  IsReadyToReview,
) => {
  if (IsFilledByClient) {
    if (IsReadyToReview) {
      return "(Ready to Review)";
    } else {
      return "(Pending with Client)";
    }
  } else {
    return "";
  }
};

// Function to compare two JSON arrays
export const areArraysEqual = (array1, array2) => {
  // Check if arrays have different lengths
  if (array1?.length !== array2?.length) {
    return false;
  }

  // Sort arrays to ensure order doesn't affect comparison
  const sortedArray1 = array1?.slice().sort();
  const sortedArray2 = array2?.slice().sort();

  // Convert arrays to strings and compare
  const string1 = JSON.stringify(sortedArray1);
  const string2 = JSON.stringify(sortedArray2);

  return string1 === string2;
};

export const locationDisplay = (formValues, isFromSharePopUp = false) => {
  const zipCodeValue = [];
  if (formValues?.zipCode) {
    formValues?.zipCode.map((zip) => {
      zipCodeValue.push(zip?.zipCode);
    });
  }
  const location = formValues?.location ?? formValues?.Location;
  const city = formValues?.city ?? formValues?.City;
  const state = formValues?.state ?? formValues?.State;
  const mapRadius = formValues?.mapRadius ?? formValues?.Radius;

  const drawnPolygon = formValues?.drawnPolygon ?? formValues?.DrawnPolygonText;

  let text = "";
  if (zipCodeValue?.length > 0) {
    text = isFromSharePopUp
      ? "Within ZIP(s) code " + zipCodeValue.join(", ")
      : "Within ZIP(s) code " + zipCodeValue.join(", ");
  } else if (drawnPolygon) {
    text = "Custom area in " + city + ", " + state;
  } else if (location) {
    text = isFromSharePopUp
      ? "in " + city + ", " + state
      : "Within " + mapRadius + " mi of " + city + ", " + state;
  }
  return text;
};
export const handleKeyDownToPreventMinusSign = (event) => {
  // Prevent the "-" character from being entered
  if (event.key === "-") {
    event.preventDefault();
  }
  const inputValue = parseFloat(event.target.value + event.key);
  // If the entered value is less than 0.25 and it's not a backspace key, prevent default behavior
  if (inputValue < 0.25 && event.key !== "Backspace") {
    event.preventDefault();
  }
};

export const handleKeyDownToPreventCharacters = (event) => {
  // Prevent the "-" character from being entered
  if (
    !/^\d$/.test(event.key) && // Check if the key pressed is a digit
    // event.key !== '.' && // Check if the key pressed is a period
    event.key !== "Backspace" && // Allow backspace
    event.key !== "Delete" && // Allow delete
    event.key !== "ArrowLeft" && // Allow arrow left
    event.key !== "ArrowRight" && // Allow arrow right
    event.key !== "Tab" && // Allow tab
    event.key !== "Home" && // Allow home
    event.key !== "End" && // Allow end
    event.key !== "Enter" && // Allow enter
    event.key !== "Escape" // Allow escape
  ) {
    event.preventDefault();
  }
};

export const locationDisplayInListing = (formValues) => {
  const zipCodeValue = formValues?.zipCode;
  // const zipCodeValue =  [97402,97403,97405]
  const location = formValues?.location ?? formValues?.Location;
  const city = formValues?.city ?? formValues?.City;
  const state = formValues?.state ?? formValues?.State;
  const mapRadius = formValues?.mapRadius ?? formValues?.Radius;
  const drawnPolygon = formValues?.drawnPolygon ?? formValues?.DrawnPolygonText;

  let text = "";
  if (zipCodeValue?.length > 0) {
    text = truncate("Within ZIP code " + zipCodeValue.join(", "), 40);
  } else if (drawnPolygon) {
    text = "Custom area in " + city + ", " + state;
  } else if (location) {
    text = mapRadius + " mi of " + city + ", " + state;
  } else {
    text = "No location added";
  }
  return text;
};

/**
 * function to get title text in home screen
 */
export const getTitleText = (
  userLocation,
  isGitchaGenieFilter,
  isCityInPreLaunch,
  getLocationText,
) => {
  if (isCityInPreLaunch) {
    return <h2>Coming soon!</h2>;
  } else if (
    userLocation &&
    !userLocation?.isFromAvailableCities &&
    !isGitchaGenieFilter
  ) {
    return (
      <h2>
        {userLocation?.city && userLocation?.state
          ? userLocation?.city + ", " + userLocation?.state + " isn't available"
          : userLocation?.formatted_address + " isn't available"}
      </h2>
    );
  } else if (userLocation && !isGitchaGenieFilter)
    return <h2 className="title-text">{getLocationText()}</h2>;
  else if (isGitchaGenieFilter) {
    return <h2 className="title-text">{getLocationText()}</h2>;
  } else return "";
};

/**
 * function to get title text in home screen in mobile view
 */
export const getTitleTextMobile = (
  userLocation,
  isGitchaGenieFilter,
  isCityInPreLaunch,
  getLocationText,
) => {
  if (isCityInPreLaunch) {
    return "Coming soon!";
  } else if (
    userLocation &&
    !userLocation?.isFromAvailableCities &&
    !isGitchaGenieFilter
  ) {
    return userLocation?.city && userLocation?.state
      ? userLocation?.city + ", " + userLocation?.state + " isn't available"
      : userLocation?.formatted_address + " isn't available";
  } else if (userLocation && !isGitchaGenieFilter) return getLocationText();
  else if (isGitchaGenieFilter) {
    return getLocationText();
  } else return "";
};

export const calculateTimeLeft = (startDate) => {
  if (startDate) {
    const introAcceptedDate = moment.utc(startDate); // Replace with your UTC date string

    // Calculate expiration date
    const introExpirationDate = moment
      .utc(introAcceptedDate)
      .add(INTRODUCTION_EXPIRATION_TIME, "hours");

    // Get current UTC time
    const currentUtcTime = moment.utc();

    // Calculate time left until expiration
    const timeLeft = moment.duration(introExpirationDate.diff(currentUtcTime));

    // Access time left in hours and minutes
    const hoursLeft = timeLeft.asHours();
    const minutesLeft = timeLeft.asMinutes();
    const secondsLeft = timeLeft.asSeconds();

    return {
      hours: Math.floor(hoursLeft),
      minutes: Math.floor(minutesLeft),
      seconds: Math.floor(secondsLeft),
    };
  } else {
    return {
      hours: 0,
      minutes: 0,
      seconds: 0,
    };
  }
};
export const getLocalityFromZipCode = async (addressObject) => {
  if (addressObject?.zip) {
    const reqPayload = `address=${addressObject?.zip}&components=country:US`;
    let city = null;
    return await getGeoLocation(reqPayload).then((data) => {
      const results = _.get(data, "results");
      if (results?.length > 0) {
        const firstAddressComponent = _.get(
          results[0],
          "address_components",
          "",
        );
        if (firstAddressComponent && firstAddressComponent.length > 0) {
          const locality = firstAddressComponent.filter(function (ca) {
            return ca.types?.[0] === "locality";
          });
          if (locality.length) {
            addressObject.city = _.get(locality?.[0], "long_name", "");
          }
          return addressObject;
        } else {
          return addressObject;
        }
      }
      return addressObject;
    });
  } else {
    return addressObject;
  }
};

export const clearLocationStatesExceptLatLng = (handleFormValues) => {
  handleFormValues({
    location: "",
    city: "",
    state: "",
    zip: "",
    isGitchaAvailable: null,
  });
};

export const checkPlanCanbeUpgraded = (
  currentActivePlan,
  selectedPlan,
  selectedPeriod,
) => {
  let comparePlan = { canPlanUpgrade: true };
  if (
    currentActivePlan?.PlanName == "Member" &&
    currentActivePlan?.period === 3 &&
    selectedPlan === "Investor Member" &&
    selectedPeriod === 2
  ) {
    comparePlan = { annualToMonthly: true, canPlanUpgrade: false };
  } else if (
    currentActivePlan?.PlanName == "Member" &&
    currentActivePlan?.period === 3 &&
    selectedPlan === "Pro Member" &&
    selectedPeriod === 2
  ) {
    comparePlan = { annualToMonthly: true, canPlanUpgrade: false };
  } else if (
    currentActivePlan?.PlanName == "Investor Member" &&
    currentActivePlan?.period === 3 &&
    selectedPlan === "Pro Member" &&
    selectedPeriod === 2
  ) {
    comparePlan = { annualToMonthly: true, canPlanUpgrade: false };
  } else if (currentActivePlan?.period !== 1 && selectedPlan === "Lite") {
    comparePlan = { toLightPlan: true, canPlanUpgrade: false };
  }
  return comparePlan;
};

export const getCurrentPlanText = (currentActivePlan) => {
  if (currentActivePlan?.PlanName === INVESTOR_MEMBER_PLAN) {
    return "investor";
  } else {
    return "member";
  }
};

export const getImageForShare = (adType) => {
  if (adType === "BUY") {
    return "https://gitcha-uploads.s3.amazonaws.com/Gitcha_images/WantAd-Buy-New.png";
  } else {
    return "https://gitcha-uploads.s3.amazonaws.com/Gitcha_images/WantAd-Rent.png";
  }
};

export const getCardElementOptions = (elem) => {
  const CARD_ELEMENT_OPTIONS = {
    placeholder: elem,
    iconStyle: "solid",
    hidePostalCode: true,
    style: {
      base: {
        iconColor: "rgb(240, 57, 122)",
        color: themeColors.neutral900,
        fontSize: "16px",
        fontFamily: '"Supreme-Regular", sans-serif',
        fontSmoothing: "antialiased",
        "::placeholder": {
          color: themeColors.neutral500,
        },
      },
      invalid: {
        color: "#e74c3c",
        ":focus": {
          color: "#303238",
        },
      },
    },
  };

  return CARD_ELEMENT_OPTIONS;
};

export const formatAddressForDisplay = (formattedAddress) => {
  if (!formattedAddress) return "";
  return formattedAddress.replace(/, USA$/, "");
};
